// V2.9 Input collection configuration: the Optional tick, the type's settings, and save-time detection.

import { TYPE_ADVANCED, typeLabel } from './agent-tasks-config'
import schemaJson from './collection-configuration-schema.json'
import type {
  CollectionConfig,
  CollectionSuggestPayload,
  RCollectionSuggestion,
} from './collection-configuration-types'
import { withDetectedInputs } from './inputs'

const schema = schemaJson as {
  types: string[]
  extras: Record<string, string[]>
  revision: string
  options: Record<string, Array<{ name: string; type: string; description: string }>>
}

/** Bumps itself whenever the option models change (the generator digests them). */
export const SCHEMA_REVISION = schema.revision

const declares = (type: string | undefined, option: string) =>
  (schema.options[type ?? ''] ?? []).some((field) => field.name === option)

// Each type spells "confirm before completing" with its own key; first match wins.
const CONFIRM_KEYS = ['require_confirmation', 'confirm_before_complete', 'ask_for_confirmation']

const confirmKeyFor = (type?: string) => CONFIRM_KEYS.find((key) => declares(type, key))

export interface SettingField {
  name: string
  /** V2.5's own wording wherever the key matches its panel. */
  label: string
  /** boolean | integer | number | string | enum | array */
  kind: string
  description: string
  values?: string[]
  extra: boolean
  /** Another setting this one needs: off and locked until that one is on. */
  requires?: string
}

// V2.5's panel is the wording authors already know, so it is read out of its own config.
const v25Entry = (type: string, name: string) =>
  (TYPE_ADVANCED[type] ?? []).find(([key]) => key === name)
const v25Label = (type: string, name: string) => v25Entry(type, name)?.[1]
const v25Requires = (type: string, name: string) => v25Entry(type, name)?.[3]
const OWN_LABEL: Record<string, string> = {
  choices: 'Options',
  num_digits: 'Number of digits',
  required_fields: 'Fields to collect',
  // V2.5 calls it verify_spelling; the runner calls the same switch spell_back
  spell_back: 'Verify spelling',
  // dtmf has no TYPE_ADVANCED entry: V2.5 words both of its controls in DtmfFieldsEditor
  ask_for_confirmation: 'Confirm the digits before recording',
}

/** V2.5's own default for a toggle, except confirmation, which starts clear on every type. */
export const settingDefault = (type: string, name: string): boolean =>
  name === confirmKeyFor(type) ? false : ((v25Entry(type, name)?.[2] as boolean) ?? false)

const EXTRA_KIND: Record<string, string> = {
  choices: 'array',
  num_digits: 'integer',
  spell_back: 'boolean',
}
const EXTRA_DESCRIPTION: Record<string, string> = {
  choices: 'The options the agent reads out.',
  num_digits: 'How many keys the caller presses.',
  spell_back: 'The agent spells the name back, letter by letter.',
}

/** Every option this type declares, extras first; the Optional tick has its own box. */
export const settingFieldsFor = (type?: string): SettingField[] => {
  if (!type) return []
  const extras = (schema.extras[type] ?? []).map((name) => ({
    name,
    label: OWN_LABEL[name] ?? v25Label(type, name) ?? name,
    kind: EXTRA_KIND[name] ?? 'string',
    description: EXTRA_DESCRIPTION[name] ?? '',
    extra: true,
  }))
  const options = (schema.options[type] ?? []).map((field) => ({
    name: field.name,
    label: OWN_LABEL[field.name] ?? v25Label(type, field.name) ?? field.name,
    kind: field.type,
    description: field.description,
    values: (field as { values?: string[] }).values,
    extra: false,
    requires: v25Requires(type, field.name),
  }))
  return [...extras, ...options]
}

/** Read one setting's current value, wherever this type keeps it. */
export const settingValue = (
  collection: CollectionConfig | undefined | null,
  field: SettingField,
) =>
  field.extra
    ? (collection as Record<string, unknown> | undefined)?.[field.name]
    : collection?.options?.[field.name]

/** Edit one setting. `undefined` clears it from the configuration and from the overrides. */
export const applySetting = (
  collection: CollectionConfig | undefined | null,
  field: SettingField,
  value: unknown,
): CollectionConfig => {
  const settings = { ...collection?.overrides?.settings }
  const options = { ...collection?.options }
  const next: CollectionConfig = { ...(collection ?? {}), options }
  if (value === undefined) {
    delete settings[field.name]
    if (field.extra) delete (next as Record<string, unknown>)[field.name]
    else delete options[field.name]
  } else {
    settings[field.name] = value
    if (field.extra) (next as Record<string, unknown>)[field.name] = value
    else options[field.name] = value
  }
  // the runner refuses both modes at once, so writing a list turns the call mode off
  if (field.name === 'choices' && value !== undefined) next.choices_from_call = false
  next.overrides = { ...collection?.overrides, settings }
  if (!Object.keys(settings).length) delete next.overrides.settings
  next.version = null
  next.stale = true
  if (value === true) return next
  // a setting that needed this one cannot stand without it, as V2.5's applyAdvancedChange does
  let out = next
  for (const dependent of settingFieldsFor(next.type)) {
    if (dependent.requires === field.name) out = applySetting(out, dependent, undefined)
  }
  return out
}

export type TickName = 'optional'

/** The twelve collect types, for the row's Type dropdown. Generated, never a hand-written list. */
export const TYPE_OPTIONS = schema.types.map((value) => ({ value, label: typeLabel(value) }))

/** Bottom right of every row, exactly as V2.5's own Optional box sits. */
export const OPTIONAL_TICK = {
  name: 'optional' as TickName,
  label: 'Optional',
  tooltip:
    'The agent moves on when the caller declines. A required value is asked once more, then left.',
}

export const coerceToType = (
  collection: CollectionConfig | undefined | null,
  type: string,
): CollectionConfig => {
  const declared = new Set((schema.options[type] ?? []).map((field) => field.name))
  const extras = schema.extras[type] ?? []
  let next: CollectionConfig = {
    ...(collection ?? {}),
    type,
    options: Object.fromEntries(
      Object.entries(collection?.options ?? {}).filter(([key]) => declared.has(key)),
    ),
  }
  if (!extras.includes('choices')) {
    const { choices: _c, choices_from_call: _f, ...rest } = next
    next = rest
  }
  if (!extras.includes('num_digits')) {
    const { num_digits: _n, ...rest } = next
    next = rest
  }
  if (!extras.includes('spell_back')) {
    const { spell_back: _s, ...rest } = next
    next = rest
  }
  // the dropped_settings the runner reported were about the old type
  const { dropped_settings: _d, ...cleaned } = next
  next = cleaned
  // Optional applies to every type, so it simply follows the move
  if (next.overrides?.optional !== undefined)
    next = writeTick(next, 'optional', next.overrides.optional)
  // a setting the new type has no field for is not an override of anything
  const kept = settingFieldsFor(type)
  const settings = Object.fromEntries(
    Object.entries(next.overrides?.settings ?? {}).filter(([name]) =>
      kept.some((field) => field.name === name),
    ),
  )
  next = { ...next, overrides: { ...next.overrides, settings } }
  if (!Object.keys(settings).length) delete next.overrides?.settings
  for (const field of kept) {
    const authored = settings[field.name]
    if (authored !== undefined) next = applySetting(next, field, authored)
  }
  return next
}

/** The author picked the collector type themselves: an override like any tick. */
export const applyType = (
  collection: CollectionConfig | undefined | null,
  type: string,
): CollectionConfig => {
  const coerced = coerceToType(collection, type)
  // the coerced overrides, never the pre-coercion ones the new type has no field for
  return { ...coerced, overrides: { ...coerced.overrides, type }, version: null, stale: true }
}

/** What the Optional box shows. Unticked is required, as an absent `required` is. */
export const ticksOf = (collection?: CollectionConfig | null) => ({
  optional: collection?.overrides?.optional ?? collection?.required === false,
})

/** V2.5's Optional box, inverted onto the runner's `required`. */
const writeTick = (
  collection: CollectionConfig,
  _tick: TickName,
  checked: boolean,
): CollectionConfig => ({ ...collection, required: !checked })

/** A tick the author clicked is an authored boolean and an override, so detection keeps it. */
export const applyTick = (
  collection: CollectionConfig | undefined | null,
  tick: TickName,
  checked: boolean,
): CollectionConfig => {
  const base: CollectionConfig = {
    ...(collection ?? {}),
    options: { ...collection?.options },
    overrides: { ...collection?.overrides, [tick]: checked },
    version: null,
    stale: true,
  }
  return writeTick(base, tick, checked)
}

// A 53-bit string hash (cyrb53): the fingerprint is compared, never reversed.
const digest = (value: string): string => {
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57
  for (let i = 0; i < value.length; i++) {
    const ch = value.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36)
}

export interface FingerprintParts {
  /** The Input's own identifier: a rename is a different Input, not an edited one. */
  id: string
  description: string
  /** The node's Task: "at most 20" becoming "at most 10" lives here, not in the description. */
  task: string
  overrides?: CollectionConfig['overrides']
}

/** Everything a saved configuration was derived from. Any change to any part makes it stale. */
export const fingerprintOf = (
  { id, description, task, overrides }: FingerprintParts,
  revision: string = SCHEMA_REVISION,
): string =>
  digest(
    JSON.stringify([
      id,
      description,
      task,
      revision,
      Object.entries(overrides ?? {})
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => a.localeCompare(b)),
    ]),
  )

export interface InputRowValue {
  id?: string
  variable_name?: string
  variable_instructions?: string
  collection?: CollectionConfig | null
}

export const rowFingerprint = (row: InputRowValue, task: string): string =>
  fingerprintOf({
    id: row.id ?? row.variable_name ?? '',
    description: row.variable_instructions ?? '',
    task,
    overrides: row.collection?.overrides,
  })

/** Out of date: nothing detected yet, saved while stale, or any part of the fingerprint changed. */
export const isStale = (row: InputRowValue, task: string): boolean => {
  if (!row.collection?.type) return true
  if (row.collection.stale === true) return true
  return row.collection.fingerprint !== rowFingerprint(row, task)
}

/** Detection owns the type and the derived keys; the author's overrides are re-applied on top. */
export const mergeDetected = (
  current: CollectionConfig | undefined | null,
  detected: CollectionConfig,
): CollectionConfig => {
  const overrides = current?.overrides
  let next: CollectionConfig = { ...detected, options: { ...detected.options } }
  if (overrides && Object.keys(overrides).length) next.overrides = { ...overrides }
  // an author who picked the collector type keeps it, and the detected settings are coerced to it
  if (overrides?.type && overrides.type !== next.type) next = coerceToType(next, overrides.type)
  if (overrides?.optional !== undefined) next = writeTick(next, 'optional', overrides.optional)
  // every setting the author ticked in the type's own panel wins too, false included
  for (const field of settingFieldsFor(next.type)) {
    const authored = overrides?.settings?.[field.name]
    if (authored !== undefined) next = applySetting(next, field, authored)
  }
  return next
}

// The runner forbids extra keys, so only the allow-listed configuration keys go back to it.
const WIRE_KEYS = [
  'type',
  'options',
  'choices',
  'choices_from_call',
  'num_digits',
  'spell_back',
  'fresh_question',
  'required',
  'instructions',
  'version',
] as const

/** Turns the runner's `{error, details:[{loc,msg}]}` into a line an author can act on. */
export const suggestErrorText = (error: unknown): string | undefined => {
  const details = (error as { response?: { data?: { details?: unknown } } })?.response?.data
    ?.details
  if (!Array.isArray(details) || !details.length) return undefined
  const named = details
    .map((d) => {
      const where = Array.isArray((d as { loc?: unknown[] }).loc)
        ? (d as { loc: unknown[] }).loc.filter((p) => typeof p === 'string').join(' ')
        : ''
      const why = (d as { msg?: string }).msg ?? ''
      return where ? `${where}: ${why}` : why
    })
    .filter(Boolean)
  return named.length ? named.join('; ') : undefined
}

// The three builder-only keys the runner is documented to ignore on a saved row.
export const SAVED_KEYS = [...WIRE_KEYS, 'stale', 'fingerprint', 'overrides'] as const

const only = (keys: readonly string[], collection: CollectionConfig): CollectionConfig =>
  Object.fromEntries(
    Object.entries(collection).filter(([key]) => keys.includes(key)),
  ) as CollectionConfig

/** Exactly the configuration the runner declares, and nothing else. */
export const toWire = (collection: CollectionConfig): CollectionConfig =>
  only(WIRE_KEYS, collection)

/** The save boundary: the runner's report about its answer is never persisted on the row. */
export const toSaved = (collection: CollectionConfig): CollectionConfig =>
  only(SAVED_KEYS, collection)

/** What a detection returns, minus everything that describes the answer rather than the settings. */
const asConfiguration = (collection: CollectionConfig): CollectionConfig => {
  const dropped = collection.dropped_settings
  const wire = toWire(collection)
  // dropped_settings is the runner's report, not a setting: kept locally, never echoed
  return dropped && Object.keys(dropped).length ? { ...wire, dropped_settings: dropped } : wire
}

/** The rules the runner applies, so an authored configuration it would refuse is never marked fresh. */
export const validateConfiguration = (collection?: CollectionConfig | null): string | undefined => {
  if (!collection?.type) return 'no collector type'
  const { type } = collection
  if (!schema.types.includes(type)) return `unknown type ${type}`
  if (type === 'choice' && !collection.choices?.length && !collection.choices_from_call)
    return 'choice needs choices'
  if (collection.choices_from_call && type !== 'choice')
    return `choices_from_call is choice-only, not ${type}`
  if (collection.choices_from_call && collection.choices?.length)
    return 'choices_from_call with a saved list'
  if (type === 'dtmf' && !collection.num_digits) return 'dtmf needs num_digits'
  if (collection.spell_back !== null && collection.spell_back !== undefined && type !== 'name')
    return `spell_back is name-only, not ${type}`
  const declared = new Set((schema.options[type] ?? []).map((field) => field.name))
  const set = Object.entries(collection.options ?? {})
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key]) => key)
  const undeclared = set.filter((key) => !declared.has(key))
  if (undeclared.length)
    return declared.size
      ? `${type} does not take ${undeclared.sort().join(', ')}`
      : `${type} has no settings: ${undeclared.sort().join(', ')}`
  return undefined
}

export interface DetectionRequest {
  nodeId: string
  /** The node and its Task: what the WHOLE answer was about. A row's own text is in `signatures`. */
  revision: string
  signatures: Record<string, string>
}

// the Task's whitespace is not part of what was asked, so re-spacing it is not an edit
const revisionOf = (nodeId: string, task: string) =>
  digest(JSON.stringify([nodeId, task.trim().replace(/\s+/g, ' ')]))

/** Shown when a held answer turns out to be about an older Task. */
export const STALE_ANSWER_NOTICE =
  'The Task changed while the agent was answering, so that answer was dropped. Detect again.'

const rowKey = (row: InputRowValue) => row.id ?? row.variable_name ?? ''

/** Everything detection was asked ABOUT for this row. Any change to it invalidates the answer. */
export const rowSignature = (row: InputRowValue): string =>
  JSON.stringify([row.variable_name ?? '', row.variable_instructions ?? '', row.collection ?? null])

export const requestFor = (nodeId: string, rows: InputRowValue[], task = ''): DetectionRequest => ({
  nodeId,
  revision: revisionOf(nodeId, task),
  signatures: Object.fromEntries(rows.map((row) => [rowKey(row), rowSignature(row)])),
})

/** Apply a detection result onto whatever the form holds NOW. Null means: discard it. */
export const reconcileDetected = <T extends InputRowValue>(
  current: T[],
  detected: T[],
  request: DetectionRequest,
  nodeIdNow: string,
  taskNow = '',
): T[] | null => {
  // another node, or a Task the whole answer was not about: none of it applies
  if (revisionOf(nodeIdNow, taskNow) !== request.revision) return null
  const answered = new Map(detected.map((row) => [rowKey(row), row]))
  const merged = current.map((row) => {
    const key = rowKey(row)
    const asked = request.signatures[key]
    // added since the request, or changed since it: the answer is about something else
    if (asked === undefined || asked !== rowSignature(row)) return row
    return (answered.get(key) as T | undefined) ?? row
  })
  // a proposal is a row the request never mentioned; a name the node now has is never duplicated
  const names = new Set(merged.map((row) => (row.variable_name ?? '').trim()).filter(Boolean))
  const added = detected.filter(
    (row) =>
      request.signatures[rowKey(row)] === undefined && !names.has((row.variable_name ?? '').trim()),
  )
  return [...merged, ...added]
}

export interface DetectionResult<T> {
  rows: T[]
  errors: Record<string, string>
  /** A whole-call failure, for the tab's banner. Null when the pass ran (or had nothing to do). */
  failure: string | null
  /** Something worth saying that is not a failure, e.g. an agent runner that cannot propose. */
  notice: string | null
}

export const OLD_RUNNER_NOTICE =
  'This agent runner cannot propose Inputs yet, so the ones the instructions name were added.'

export const NO_SUGGESTION = 'no suggestion' // the runner's own reason when the model made no call

/** The runner's own reason for an Input it could not configure, in the author's words. */
export const reasonNote = (reason?: string): string | undefined => {
  if (!reason) return undefined
  // the model correctly makes no call for a branching rule or an instruction that collects nothing
  return reason === NO_SUGGESTION ? 'Not a value to collect, so there is no configuration.' : reason
}

/** Fills in every Input's collection configuration; a failure never blocks the save. */
export const detectCollections = async <T extends InputRowValue>({
  authId,
  instructions,
  rows,
  suggest,
  propose = false,
  newRow,
}: {
  authId?: string
  instructions: string
  rows: T[]
  suggest: (payload: CollectionSuggestPayload) => Promise<RCollectionSuggestion>
  /** "Detect from instructions": also ask which Inputs the Task calls for. */
  propose?: boolean
  /** Builds a blank row of the caller's own shape for each proposal. */
  newRow?: (proposal: { variable_name: string; variable_instructions: string }) => T
}): Promise<DetectionResult<T>> => {
  const named = rows.filter((row) => (row.variable_name ?? '').trim())
  const stale = new Set(named.filter((row) => isStale(row, instructions)))
  // a propose pass always calls: it asks what Inputs the Task implies, not only for a refresh
  if (!propose && (!named.length || !stale.size))
    return { rows, errors: {}, failure: null, notice: null }
  const markStale = (row: T): T =>
    row.collection ? ({ ...row, collection: { ...row.collection, stale: true } } as T) : row
  if (!authId)
    return {
      rows: rows.map((row) => (stale.has(row) ? markStale(row) : row)),
      errors: {},
      failure:
        'Could not detect the collection settings: this account has no auth id yet. Reload the builder and save again.',
      notice: null,
    }

  // only fresh Inputs travel as `existing`, with a null version: the runner owns that digest
  const existing: Record<string, CollectionConfig> = {}
  for (const row of named) {
    if (!stale.has(row) && row.collection) {
      existing[row.variable_name as string] = { ...toWire(row.collection), version: null }
    }
  }

  let res: RCollectionSuggestion
  try {
    res = await suggest({
      auth_id: authId,
      instructions,
      inputs: named.map((row) => ({
        variable_name: row.variable_name as string,
        variable_instructions: row.variable_instructions ?? '',
      })),
      existing,
      overwrite: false,
      ...(propose ? { propose_inputs: true } : {}),
    })
  } catch {
    // a configuration that could not be refreshed stays marked rather than reading as current
    return {
      rows: rows.map((row) => (stale.has(row) ? markStale(row) : row)),
      errors: {},
      failure:
        'Could not reach the agent runner to detect the collection settings. The node was saved as it is.',
      notice: null,
    }
  }

  const errors: Record<string, string> = {}
  const merged = rows.map((row) => {
    const name = row.variable_name ?? ''
    const reason = res?.errors?.[name]
    if (reason) errors[name] = reason
    const detected = res?.configurations?.[name]
    // an Input the runner could not configure keeps what it had, marked out of date
    if (!detected) return stale.has(row) ? markStale(row) : row
    const collection = mergeDetected(row.collection, asConfiguration(detected))
    // a field with no collector type is not collectable, so the stored one stays, out of date
    if (!collection.type) {
      errors[name] = 'no collector type'
      return markStale(row)
    }
    // the overrides are merged on top, so the RESULT is what has to hold
    const invalid = validateConfiguration(collection)
    if (invalid) {
      errors[name] = invalid
      return { ...row, collection: { ...collection, stale: true } } as T
    }
    // refreshed: it now describes this Input, this Task and this schema revision
    const refreshed = { ...row, collection }
    return {
      ...refreshed,
      collection: {
        ...collection,
        stale: false,
        fingerprint: rowFingerprint(refreshed, instructions),
      },
    }
  })
  if (!propose) return { rows: merged, errors, failure: null, notice: null }

  // an older runner answers without the key at all, so the Inputs the Task names are added here
  if (!res?.proposed) {
    return {
      rows: withDetectedInputs(instructions, merged) as T[],
      errors,
      failure: null,
      notice: OLD_RUNNER_NOTICE,
    }
  }

  const taken = new Set(merged.map((row) => (row.variable_name ?? '').trim()).filter(Boolean))
  const added: T[] = []
  for (const proposal of res.proposed) {
    const name = (proposal.variable_name ?? '').trim()
    // an Input the node already has is never duplicated, whatever the runner proposes
    if (!name || taken.has(name)) continue
    taken.add(name)
    const blank = newRow?.({
      variable_name: name,
      variable_instructions: proposal.variable_instructions ?? '',
    })
    if (!blank) continue
    const collection = proposal.collection ? asConfiguration(proposal.collection) : undefined
    const row = (
      collection?.type ? { ...blank, collection: { ...collection, stale: false } } : blank
    ) as T
    added.push(
      collection?.type
        ? ({
            ...row,
            collection: { ...row.collection, fingerprint: rowFingerprint(row, instructions) },
          } as T)
        : row,
    )
  }
  return { rows: [...merged, ...added], errors, failure: null, notice: null }
}
